import React, { useState, useEffect, useRef } from 'react';
import { Carousel, Spinner } from 'react-bootstrap';
import styles from './NoticeComponent.module.css';

const ImageCarousel = ({ images, title, onError }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const intervalRef = useRef(null);

  // Handle manual navigation
  const handleSelect = (selectedIndex) => {
    setIndex(selectedIndex);
  };

  // Handle image load success
  const handleImageLoad = (imageUrl) => {
    setLoadedImages(prev => ({ ...prev, [imageUrl]: true }));
  };

  // Handle image load error
  const handleImageError = (e, imageUrl) => {
    console.error('Image failed to load:', imageUrl);
    setImageErrors(prev => ({ ...prev, [imageUrl]: true }));
    if (onError) {
      onError(e);
    }
  };

  // Set up auto-slide
  useEffect(() => {
    // Only set up interval if there are multiple images
    if (images.length > 1) {
      intervalRef.current = setInterval(() => {
        if (!paused) {
          setIndex((prevIndex) => (prevIndex + 1) % images.length);
        }
      }, 5000); // Change slide every 5 seconds
    }

    // Clean up
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [images.length, paused]);

  // Pause carousel on hover
  const handleMouseEnter = () => setPaused(true);
  const handleMouseLeave = () => setPaused(false);

  // If there are no images, return null
  if (!images || images.length === 0) {
    return null;
  }

  // Common image styles to ensure full content visibility
  const imageContainerStyle = {
    maxWidth: '400px',
    margin: '15px auto',
    borderRadius: '6px',
    overflow: 'hidden',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
    position: 'relative'
  };

  const imageStyle = {
    width: '100%',
    height: 'auto',
    maxHeight: '500px',
    objectFit: 'contain',
    backgroundColor: '#f8f8f8',
    transition: 'opacity 0.3s ease-in-out'
  };

  const loadingContainerStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1
  };

  // If there's only one image, render it directly without controls
  if (images.length === 1) {
    const imageUrl = images[0];
    const isLoaded = loadedImages[imageUrl];
    const hasError = imageErrors[imageUrl];

    return (
      <div className="singleImageContainer" style={imageContainerStyle}>
        {!isLoaded && !hasError && (
          <div style={loadingContainerStyle}>
            <Spinner animation="border" size="sm" />
          </div>
        )}
        <img
          src={imageUrl}
          alt={`${title || 'Notice'} image`}
          style={{
            ...imageStyle,
            opacity: isLoaded ? 1 : 0,
            minHeight: isLoaded ? 'auto' : '200px'
          }}
          onLoad={() => handleImageLoad(imageUrl)}
          onError={(e) => handleImageError(e, imageUrl)}
          loading="eager"
        />
      </div>
    );
  }

  // For multiple images, render a carousel
  return (
    <div 
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={imageContainerStyle}
    >
      <Carousel 
        activeIndex={index} 
        onSelect={handleSelect}
        interval={null}
        indicators={false}
        controls={images.length > 1}
        slide={true}
        fade={false}
        className="position-relative"
      >
        {images.map((imageUrl, idx) => {
          const isLoaded = loadedImages[imageUrl];
          const hasError = imageErrors[imageUrl];

          return (
            <Carousel.Item key={idx}>
              <div className="carousel-image-container" style={{ padding: '10px 0', position: 'relative', minHeight: '200px' }}>
                {!isLoaded && !hasError && (
                  <div style={loadingContainerStyle}>
                    <Spinner animation="border" size="sm" />
                  </div>
                )}
                <img
                  className="d-block w-100"
                  src={imageUrl}
                  alt={`${title || 'Notice'} image ${idx + 1}`}
                  style={{
                    ...imageStyle,
                    opacity: isLoaded ? 1 : 0
                  }}
                  onLoad={() => handleImageLoad(imageUrl)}
                  onError={(e) => handleImageError(e, imageUrl)}
                  loading="eager"
                />
              </div>
            </Carousel.Item>
          );
        })}
      </Carousel>
      
      {/* Custom circular navigation dots */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        padding: '10px 0 15px',
        backgroundColor: 'rgba(0,0,0,0.03)'
      }}>
        {images.map((_, idx) => (
          <button
            key={idx}
            onClick={() => handleSelect(idx)}
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              margin: '0 5px',
              padding: 0,
              backgroundColor: idx === index ? '#007bff' : '#ccc',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.3s ease'
            }}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageCarousel;